import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Users, Building2, ArrowRight, Megaphone, DollarSign, AlertTriangle, ShieldCheck } from "lucide-react";
import { sr, actAs } from "../../lib/sr";

const money = (n) => "$" + Number(n || 0).toLocaleString();
const TONE = { red: "#B42318", amber: "#B8791B", green: "#067647" };

export default function MyClients({ embedded = false }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { sr.myClients().then(setData).catch(() => setData({ error: true })); }, []);

  if (!data) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  if (data.error) return <div className="sr-card"><p className="sr-muted">Could not load your assigned clients.</p></div>;

  const manage = (c) => { actAs.set(c.id, c.name); sr.adminActAs(c.id).catch(() => {}); window.location.assign("/app"); };
  const clients = data.clients || [];

  return (
    <div data-testid="my-clients-page">
      <div style={{ marginBottom: 16 }}>
        <span className="sr-badge" style={{ marginBottom: 8 }}><Users size={12} /> Your book of business</span>
        <h1 style={{ fontSize: embedded ? 26 : 30 }}>Clients assigned to you</h1>
        <p className="sr-muted" style={{ marginTop: 6 }}>Jump straight into a client's workspace to manage their growth. This is just your accounts — no scrolling the whole portfolio.</p>
      </div>

      <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12, marginBottom: 18 }} data-testid="my-clients-totals">
        <div className="sr-stat"><div className="sr-stat-label">Assigned clients</div><div className="sr-stat-val" data-testid="my-clients-count">{data.assigned_count}</div></div>
        <div className="sr-stat"><div className="sr-stat-label">Live campaigns</div><div className="sr-stat-val">{data.live_campaigns}</div></div>
        <div className="sr-stat"><div className="sr-stat-label">Monthly budget</div><div className="sr-stat-val">{money(data.monthly_budget)}</div></div>
        <div className="sr-stat"><div className="sr-stat-label">Need attention</div><div className="sr-stat-val" style={{ color: data.attention ? TONE.amber : undefined }}>{data.attention}</div></div>
      </div>

      {clients.length === 0 ? (
        <div className="sr-card sr-empty" data-testid="my-clients-empty" style={{ textAlign: "center", padding: 36 }}>
          <ShieldCheck size={26} style={{ opacity: 0.4 }} />
          <p style={{ marginTop: 10 }}>No clients are assigned to you yet.</p>
          {data.is_admin && <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 12 }} onClick={() => nav("/app/admin/clients")}>Browse all clients</button>}
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {clients.map((c) => (
            <div key={c.id} className="sr-card sr-card-hover" data-testid={`my-client-${c.id}`}
              onClick={() => manage(c)} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
              <span className="sr-pill-ico" style={{ background: "var(--blue-light)", color: "var(--blue)", flexShrink: 0 }}><Building2 size={18} /></span>
              <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                <div style={{ color: "var(--navy)", fontWeight: 700, fontSize: 15 }}>{c.name}</div>
                <div className="sr-muted" style={{ fontSize: 12, display: "flex", gap: 10, flexWrap: "wrap", marginTop: 2 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Megaphone size={12} /> {c.live_campaigns} live</span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><DollarSign size={12} /> {money(c.monthly_budget)}/mo</span>
                  <span style={{ textTransform: "capitalize" }}>· {String(c.execution_mode || "").toLowerCase()}</span>
                </div>
              </div>
              {c.health?.tone && c.health.tone !== "green" && (
                <span className="sr-badge" style={{ color: TONE[c.health.tone] }} data-testid={`my-client-health-${c.id}`}>
                  <AlertTriangle size={12} /> {c.health.label || c.health.tone}
                </span>
              )}
              <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={(e) => { e.stopPropagation(); manage(c); }} data-testid={`my-client-manage-${c.id}`} style={{ flexShrink: 0 }}>
                Manage <ArrowRight size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
